import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const LiveProjectsSection = ({ liveProjects }) => {
  const [currentProject, setCurrentProject] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    if (!isAutoPlaying || !liveProjects?.length) return;

    const interval = setInterval(() => {
      setCurrentProject((prev) => (prev + 1) % liveProjects.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, liveProjects?.length]);

  const handleProjectChange = (index) => {
    setCurrentProject(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const project = liveProjects?.[currentProject];

  if (!project) {
    return (
      <div className="bg-slate-950/90 rounded-2xl p-8 text-white text-center">
        <p className="text-white text-lg font-semibold">No live projects available.</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-slate-950 to-blue-950 rounded-2xl p-4 sm:p-8 text-white">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-3">
          <div className="w-3 h-3 bg-blue-400 rounded-full animate-pulse"></div>
          <h3 className="text-xl sm:text-2xl font-bold">Live Projects</h3>
          <span className="px-3 py-1 bg-blue-400/20 text-blue-400 text-xs sm:text-sm rounded-full">
            Real-time Data
          </span>
        </div>

        <button
          onClick={() => setIsAutoPlaying(!isAutoPlaying)}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
        >
          <Icon name={isAutoPlaying ? 'Pause' : 'Play'} size={16} />
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Project Showcase */}
        <div className="space-y-6">
          <div className="relative rounded-xl overflow-hidden">
            <Image
              src={project.image}
              alt={project.title}
              className="w-full h-48 sm:h-64 object-cover"
            />
            <div className="absolute top-4 right-4">
              <span className="px-3 py-1 bg-blue-400 text-white text-sm rounded-full flex items-center space-x-1">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                <span>Live</span>
              </span>
            </div>
          </div>

          <div>
            <h4 className="text-xl font-bold mb-2">{project.title}</h4>
            <p className="text-blue-100/80 mb-4">{project.description}</p>
            
            <div className="flex flex-wrap gap-3">
              <Button
                variant="default"
                iconName="ExternalLink"
                iconPosition="right"
                className="bg-blue-600 text-white hover:bg-blue-700"
                onClick={() => { /* TODO: Open project URL */ }}
              >
                Visit Site
              </Button>
              <Button
                variant="outline"
                iconName="BarChart3"
                iconPosition="right"
                className="border-blue-400/50 text-blue-100 hover:bg-blue-400/10"
                onClick={() => { /* TODO: Open analytics URL */ }}
              >
                View Analytics
              </Button>
            </div>
          </div>
        </div>

        {/* Real-time Metrics & Feedback */}
        <div className="space-y-6">
          <div>
            <h5 className="text-lg font-semibold mb-4">Real-time Metrics</h5>
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(project.liveMetrics || {}).map(([key, value]) => (
                <div key={key} className="flex-1 min-w-[45%] bg-blue-400/10 rounded-lg p-4 items-center text-center">
                  <div className="text-2xl font-bold text-blue-400 mb-1">{value}</div>
                  <div className="text-xs sm:text-sm text-blue-100/70 capitalize">
                    {key.replace(/([A-Z])/g, ' $1')}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {project.recentFeedback && (
            <div>
              <h5 className="text-lg font-semibold mb-4">Recent User Feedback</h5>
              <div className="space-y-3">
                {project.recentFeedback.map((feedback, index) => (
                  <div key={index} className="bg-blue-400/10 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Icon
                            key={i}
                            name="Star"
                            size={14}
                            className={i < feedback.rating ? 'text-blue-400 fill-current' : 'text-white/30'}
                          />
                        ))}
                      </div>
                      <span className="text-sm text-blue-100/70">{feedback.date}</span>
                    </div>
                    <p className="text-sm text-white/80">"{feedback.comment}"</p>
                    <div className="text-xs text-blue-100/60 mt-1">- {feedback.user}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <h5 className="text-lg font-semibold mb-3">Technology Stack</h5>
            <div className="flex flex-wrap gap-2">
              {project.technologies?.map((tech, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-400/10 text-blue-400 text-sm rounded-md"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Project Navigation */}
      <div className="flex items-center justify-center space-x-2 mt-8">
        {liveProjects.map((_, index) => (
          <button
            key={index}
            onClick={() => handleProjectChange(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentProject ? 'bg-blue-400 scale-125' : 'bg-white/30 hover:bg-white/50'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default LiveProjectsSection;